import { useEffect, useMemo, useRef, useState, } from 'react';
import { format as formatDate, addDays, } from 'date-fns';
import { useAsync, useToggle, useCounter, } from 'react-use';
import { keyBy, orderBy, groupBy, uniq, } from 'lodash';
import { toast } from 'react-toastify';

import firebase, { functions } from '../../firebase';
import { getCollectionData, getDocumentData, } from '../../shared/firebase';
import PublicPage from '../hocs/PublicPage';
import TenantUserPage from '../hocs/TenantUserPage';
import RichTextContent from '../RichTextContent';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useCollectionsFetch from '../hooks/useCollectionsFetch';
import { prefectures } from '../../shared/config';
import useAppTitle from '../hooks/useAppTitle';
import EditButton from '../EditButton';
import ModelFormModal from '../modals/ModelFormModal';
import DeleteButton from '../DeleteButton';

const { entries, keys, } = Object;
const db = firebase.firestore();
const fetchUserVoiceData = functions.httpsCallable('fetchUserVoiceData');

function UserVoiceContent(props) {
  const { history, user, tenant, userVoice, match: { params: { userVoiceId }, }, } = props;
  const [version, { inc: updateVersion }] = useCounter(0);
  const questions = useCollectionSubscriptionInTenant(db.collection('questions'));
  const questionsById = keyBy(questions, 'id');
  useAppTitle(userVoice?.title);
  const { value: surveyAnswers, loading: isLoadingSurveyAnswers = false } = useAsync(async () => {
    const { data } = await fetchUserVoiceData({
      userVoiceId,
    });
    if (data?.surveyAnswers) {
      return data.surveyAnswers.map((surveyAnswer) => {
        return {
          ...surveyAnswer,
          createdAt: { toDate: _ => new Date(surveyAnswer.createdAt._seconds * 1000), },
        };
      });
    }
    // Fallback for non-public user voices (staff/admin access)
    const surveyAnswerUserVoices = await getCollectionData(
      db.collection('surveyAnswerUserVoices').where('userVoiceId', '==', userVoiceId)
    );
    const surveyAnswerIds = [...new Set(surveyAnswerUserVoices.map(_ => _.surveyAnswerId))];
    const surveyAnswers = (await Promise.all(
      surveyAnswerIds.map(_ => getDocumentData(db.doc(`surveyAnswers/${_}`)))
    )).filter(_ => _);
    return surveyAnswers;
  }, [userVoiceId, version]);
  const sortedSurveyAnswers = useMemo(_ => orderBy(surveyAnswers, _ => _.createdAt.toDate(), 'desc'), [surveyAnswers]);
  const surveyAnswerUserVoices = useCollectionsFetch(
    uniq((surveyAnswers || []).map(_ => _.id)).map(surveyAnswerId =>
      db.collection('surveyAnswerUserVoices').where('surveyAnswerId', '==', surveyAnswerId).where('userVoiceId', '==', userVoiceId)
    ),
    [(surveyAnswers || []).map(_ => _.id).join(','), userVoiceId, version]
  ).flat();
  const groupedSurveyAnswerUserVoicesBySurveyAnswerId = useMemo(_ =>
    groupBy(surveyAnswerUserVoices, _ => _.surveyAnswerId),
    [surveyAnswerUserVoices]
  );
  useEffect(() => {
    if(userVoice?.isNoindex && ![...(document.head.childNodes || [])].some(_ => _.getAttribute?.('name') === 'robots')) {
      const metaElm = document.createElement('meta');
      metaElm.setAttribute('name', 'robots');
      metaElm.setAttribute('content', 'noindex');
      document.head.appendChild(metaElm);
    }
  }, [userVoice?.isNoindex]);
  useEffect(() => {
    if(user != null && !userVoice?.isPublic && !['admin', 'staff'].includes(user.role)) {
      history.push(`/${tenant.id}`);
      toast.error('非公開です');
      return null;
    }
  }, [user?.role, userVoice]);


  return (
    <div className={`container p-3 p-md-5`}>
      <div className="row">
        <div className={`col col-12 d-flex flex-column gap-5`}>
          <h1 className="h3 text-center">{userVoice?.title}</h1>
          <RichTextContent className="text-left" html={userVoice?.description} />
          <div className="d-flex flex-column gap-2">
            {
              sortedSurveyAnswers.map((surveyAnswer) => {
                const surveyAnswerUserVoicesForAnswer = groupedSurveyAnswerUserVoicesBySurveyAnswerId[surveyAnswer.id] || [];
                return (
                  <SurveyAnswerContent
                    key={surveyAnswer.id}
                    surveyAnswer={surveyAnswer}
                    questionsById={questionsById}
                    surveyAnswerUserVoices={surveyAnswerUserVoicesForAnswer}
                    user={user}
                    tenant={tenant}
                    userVoiceId={userVoiceId}
                    updateVersion={updateVersion}
                  />
                );
              })
            }
          </div>
        </div>
      </div>
    </div>
  );
};

function SurveyAnswerContent (props) {
  const { surveyAnswer, questionsById, surveyAnswerUserVoices = [], user, tenant, userVoiceId, updateVersion, } = props;
  const [isExpanded, toggleExpanded] = useToggle(false);
  const [shouldCollapse, setShouldCollapse] = useState(false);
  const [actualHeight, setActualHeight] = useState(0);
  const contentRef = useRef(null);
  const surveyAnswerUserVoicesByQuestionId = keyBy(surveyAnswerUserVoices, _ => _.questionId);
  const questionIdsWithUserVoice = surveyAnswerUserVoices.map(_ => _.questionId);
  const canEdit = user && ['admin', 'staff'].includes(user.role) && user.tenantId === tenant.id;
  const surveyAnswerRef = surveyAnswer.ref || db.doc(`surveyAnswers/${surveyAnswer.id}`);
  const { userVoiceDisplayNames = {}, userVoiceHeaders = {} } = surveyAnswer;
  const rows = questionIdsWithUserVoice.map(questionId => {
    const surveyAnswerUserVoice = surveyAnswerUserVoicesByQuestionId[questionId];
    const question = questionsById[questionId];
    const answer = surveyAnswer.answers?.[questionId];
    return {
      questionId,
      question,
      answer,
      surveyAnswerUserVoice,
    };
  });

  const userVoiceHeader = surveyAnswer.userVoiceHeaders?.[surveyAnswerUserVoices[0]?.userVoiceId];

  useEffect(() => {
    if (contentRef.current) {
      const height = contentRef.current.scrollHeight;
      setActualHeight(height);
      setShouldCollapse(height > 150);
    }
  }, [rows]);

  return (
    <div className="border rounded p-3" onClick={shouldCollapse ? toggleExpanded : undefined} style={{ cursor: shouldCollapse ? 'pointer' : 'default' }}>
      <div style={{ position: 'relative' }}>
        <div ref={contentRef} style={{ maxHeight: shouldCollapse && !isExpanded ? '120px' : `${actualHeight}px`, overflow: 'hidden', transition: 'max-height 0.3s ease-in-out' }}>
          {
            userVoiceHeader && (
              <h2 className="h6 p-2 rounded bg-light-grey mb-3" style={{ whiteSpace: 'pre-line' }}>
                {userVoiceHeader}
              </h2>
            )
          }
          <div className="d-flex flex-column gap-3">
            {
              rows.map(({ questionId, question, answer, surveyAnswerUserVoice }) => {
                const displayContent = surveyAnswerUserVoice?.displayContent;
                const displayValue = displayContent || {
                  text: answer,
                  checkbox: keys(answer || {}).join(', '),
                  radio: answer,
                  select: answer,
                  imageFile: [answer].flat().map((answer, i) => (
                    <div key={i}>
                      <img src={answer?.url} style={{ maxWidth: '100%' }}></img>
                    </div>
                  )),
                }[question?.type || ''];
                const surveyAnswerUserVoiceRef = surveyAnswerUserVoice?.ref || db.doc(`surveyAnswerUserVoices/${surveyAnswerUserVoice?.id}`);
                return (
                  <div key={questionId}>
                    <div className="small text-muted">{question?.description}</div>
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {displayValue}
                    </div>
                    {canEdit && surveyAnswerUserVoice && (
                      <div className="d-flex align-items-center gap-1 mt-1">
                        <EditButton
                          label={false}
                          color="link"
                          size="sm"
                          className="p-0"
                          itemRef={surveyAnswerUserVoiceRef}
                          FormModal={ModelFormModal}
                          formProps={{
                            title: 'ユーザーズボイス編集',
                            fields: {
                              displayName: {
                                label: '表示名',
                                type: 'string',
                                validations: {
                                  required: v => v && v.trim().length > 0,
                                },
                              },
                              userVoiceHeader: {
                                label: 'ユーザーズボイス見出し',
                                type: 'text',
                              },
                              displayContent: {
                                label: '表示内容',
                                type: 'text',
                                hidden: _ => question?.type === 'imageFile',
                                validations: {
                                  required: v => question?.type === 'imageFile' || (v && v.trim().length > 0),
                                },
                              },
                            },
                          }}
                          beforeEdit={(item) => ({
                            displayName: userVoiceDisplayNames?.[userVoiceId] || '',
                            userVoiceHeader: userVoiceHeaders?.[userVoiceId] || '',
                            displayContent: item.displayContent || '',
                          })}
                          beforeSubmit={async (values) => {
                            const { displayName, userVoiceHeader, displayContent } = values;
                            await surveyAnswerRef.update({
                              userVoiceDisplayNames: {
                                ...(userVoiceDisplayNames || {}),
                                [userVoiceId]: displayName,
                              },
                              userVoiceHeaders: {
                                ...(userVoiceHeaders || {}),
                                [userVoiceId]: userVoiceHeader,
                              },
                            });
                            return {
                              displayContent,
                            };
                          }}
                          onFinish={_ => updateVersion()}
                        />
                        <DeleteButton
                          label={false}
                          color="link"
                          size="sm"
                          className="p-0"
                          itemRef={surveyAnswerUserVoiceRef}
                          onFinish={_ => updateVersion()}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            }
          </div>
        </div>
        {
          shouldCollapse && !isExpanded && (
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '30px',
              background: 'linear-gradient(to bottom, transparent, white)',
              pointerEvents: 'none',
            }} />
          )
        }
      </div>
      {
        shouldCollapse && (
          <div className="text-center small text-primary mt-2">
            {isExpanded ? '閉じる' : 'もっと見る'}
          </div>
        )
      }
      <div className="mt-4 d-flex justify-content-end gap-1 small">
        <div>
          {formatDate(surveyAnswer.createdAt.toDate(), 'yyyy/MM/dd')}
        </div>
        <div>
          {prefectures[surveyAnswer.createdBy.prefecture]}
        </div>
        <div>
          {surveyAnswer.userVoiceDisplayNames?.[surveyAnswerUserVoices[0]?.userVoiceId]}
        </div>
      </div>
    </div>
  );
}

export default function UserVoice(props) {
  const { match: { params: { tenantPath, userVoiceId }, }, } = props;
  const userVoice = useDocumentSubscription(db.collection('userVoices').doc(userVoiceId), [userVoiceId]);
  const Content = userVoice?.isPublic ? PublicPage(UserVoiceContent) : TenantUserPage(UserVoiceContent);
  return <Content {...props} userVoice={userVoice} />;
}
